import {useEffect, useRef} from 'react';
import type {FacebookConnectCallback, VoidFunction} from '@Components/login-page/login-page.types';
import {dismissIOSSafariView, getOauthUrl, openLoginWindow, SSO_OAUTH_COOKIE_POLL_INTERVAL_MS, SSO_OAUTH_DATA_COOKIE} from '@Libraries/login-signup-library';
import {GA4EventName} from '@Libraries/ga-events';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {deleteCookie, readCookie} from '@Utils/cookie.util';
import {noop} from '@Utils/general.util';
import {getStateFromUrl} from '@Utils/url.util';

type ExistingSsoAccountHandler = (identityToken: string, email: string, password: string) => Promise<void>;

interface UseFacebookSignInParams {
  onConnect?: FacebookConnectCallback;
}

interface UseFacebookSignInReturn {
  /** Opens Facebook's consent window and finishes the log-in or sign-up once the callback page answers. */
  signInWithFacebook: () => Promise<void>;
  /** The same window for a logged-in user linking Facebook to their account; a clash with another account is not offered a merge. */
  connectFacebookAccount: () => Promise<void>;
}

/** Facebook has no post-login redirect of its own; the page that opened the window decides where to go. */
const FACEBOOK_REDIRECT_URL = '';

/**
 * The Facebook OAuth handshake that `ButtonFacebookLogin` and the saved-accounts `Continue` share. The consent
 * window reports back through the `pmwsac` cookie, which this polls; the iOS app and the embedded editor
 * cannot see that cookie and ask the server instead.
 */
export const useFacebookSignIn = ({onConnect = noop}: UseFacebookSignInParams = {}): UseFacebookSignInReturn => {
  const oauthState = useRef('');
  const cookiePollInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return (): void => {
      clearCookiePoll();
    };
  }, []);

  const clearCookiePoll = (): void => {
    if (cookiePollInterval.current) {
      clearInterval(cookiePollInterval.current);
      deleteCookie(SSO_OAUTH_DATA_COOKIE);
    }
  };

  const onLoginUrlFetched = (url: string): void => {
    clearCookiePoll();
    oauthState.current = getStateFromUrl(url);
  };

  const handleExistingAccount: ExistingSsoAccountHandler = async (identityToken, email, password) => {
    const response = (await window.PMW.writeLocal('authenticate/handleExistingSSOAccount', {
      identityToken,
      email,
      password,
      source: GA4EventName.SIGN_UP_FACEBOOK,
    })) as AjaxSuccessResponse;
    window.PMW.ssoAccountAuthenticationCallback(response, handleExistingAccount, FACEBOOK_REDIRECT_URL, onConnect, GA4EventName.SIGN_UP_FACEBOOK);
  };

  const checkLoggedInStatus = async (): Promise<void> => {
    const response = (await window.PMW.pollingAjaxCallAsync(
      (): Promise<void> => {
        return Promise.resolve();
      },
      () => {
        return window.PMW.readLocal('authenticate/hasAccountBeenLoggedIn', {state: oauthState.current});
      }
    )) as AjaxSuccessResponse;
    dismissIOSSafariView();
    window.PMW.ssoAccountAuthenticationCallback(response, handleExistingAccount, FACEBOOK_REDIRECT_URL, onConnect, GA4EventName.SIGN_UP_FACEBOOK);
  };

  const startFacebookOauth = async (onExistingAccount: ExistingSsoAccountHandler | VoidFunction): Promise<void> => {
    const loginWindow = await openLoginWindow(getOauthUrl.bind(null, 'authenticate/getFacebookOauthUrl'), onLoginUrlFetched);
    if (isIOSAppOrEmbeddedEditor()) {
      void checkLoggedInStatus();
      return;
    }
    cookiePollInterval.current = setInterval(() => {
      const cookie = readCookie(SSO_OAUTH_DATA_COOKIE);
      if (cookie) {
        clearCookiePoll();
        loginWindow?.close();
        window.PMW.ssoAccountAuthenticationCallback(JSON.parse(decodeURIComponent(cookie)), onExistingAccount, FACEBOOK_REDIRECT_URL, onConnect, GA4EventName.SIGN_UP_FACEBOOK);
      }
    }, SSO_OAUTH_COOKIE_POLL_INTERVAL_MS);
  };

  const signInWithFacebook = (): Promise<void> => {
    return startFacebookOauth(handleExistingAccount);
  };

  const connectFacebookAccount = (): Promise<void> => {
    return startFacebookOauth(noop);
  };

  return {signInWithFacebook, connectFacebookAccount};
};
